export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_SOFT_BYTES = 3 * 1024 * 1024;

export const PHOTO_SIZE_HINT =
  "Please keep each photo between 3 and 5 MB. Larger photos are compressed on this device before they are saved with your account. If a photo is still too large after compression, choose a smaller image or a lower-resolution export from your camera roll.";

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That photo could not be read."));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

export async function compressImageFile(file, { maxBytes = PHOTO_MAX_BYTES, maxEdge = 1920 } = {}) {
  if (!file || !String(file.type || "").startsWith("image/")) return file;
  if (file.size <= PHOTO_SOFT_BYTES) return file;

  const image = await loadImage(file);
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const type = file.type === "image/png" ? "image/jpeg" : file.type || "image/jpeg";
  let quality = 0.82;
  let blob = await canvasToBlob(canvas, type, quality);
  while (blob && blob.size > maxBytes && quality > 0.45) {
    quality -= 0.12;
    blob = await canvasToBlob(canvas, type, quality);
  }
  if (!blob || blob.size > maxBytes) {
    const error = new Error(PHOTO_SIZE_HINT);
    error.code = "photo-too-large";
    throw error;
  }
  const name = file.name.replace(/\.(png|webp|heic|jpeg|jpg)$/i, ".jpg");
  return new File([blob], name, { type: blob.type || "image/jpeg", lastModified: Date.now() });
}

export function photoTooLargeMessage(file) {
  if (!file) return PHOTO_SIZE_HINT;
  const mb = (file.size / (1024 * 1024)).toFixed(1);
  return `This photo is ${mb} MB. ${PHOTO_SIZE_HINT}`;
}
